import { useState, useEffect } from 'react';
import { FileText, Plus, RefreshCw, Trash2, Edit3 } from 'lucide-react';
import { API_BASE } from '../config';

export default function ScriptLibrary() {
  const [scripts, setScripts] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState<any>(null);
  const [form, setForm] = useState({ name: '', description: '', type: 'cmd', content: '' });
  const [formError, setFormError] = useState('');
  const [saving, setSaving] = useState(false);

  const fetchScripts = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`${API_BASE}/api/scripts`, {
        credentials: 'include',
      });
      const data = await res.json();
      if (res.ok) setScripts(data);
      else setError(data.error || 'Failed to load scripts');
    } catch { setError('Failed to load scripts'); }
    setLoading(false);
  };

  useEffect(() => { fetchScripts(); }, []);

  const saveScript = async () => {
    setSaving(true);
    setFormError('');
    try {
      const url = editing
        ? `${API_BASE}/api/scripts/${editing.id}`
        : `${API_BASE}/api/scripts`;
      const method = editing ? 'PUT' : 'POST';
      const res = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(form),
      });
      const data = await res.json();
      if (res.ok) {
        setShowForm(false);
        setEditing(null);
        setForm({ name: '', description: '', type: 'cmd', content: '' });
        fetchScripts();
      } else {
        setFormError(data.error || 'Failed to save script');
      }
    } catch { setFormError('Failed to save script'); }
    setSaving(false);
  };

  const deleteScript = async (id: string) => {
    if (!confirm('Delete this script?')) return;
    try {
      await fetch(`${API_BASE}/api/scripts/${id}`, {
        method: 'DELETE',
        credentials: 'include',
      });
      fetchScripts();
    } catch {}
  };

  const typeBadgeColor: Record<string, string> = {
    cmd: 'var(--accent-primary)',
    bat: 'var(--warning)',
    ps1: '#8b5cf6',
  };

  return (
    <div style={{ padding: '0 1rem 1rem 1rem' }}>
      <div className="glass-panel" style={{ padding: '1.5rem' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
          <h2 style={{ margin: 0, display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <FileText size={24} color="#8b5cf6" /> Script Library
          </h2>
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <button className="btn btn-secondary" onClick={fetchScripts}>
              <RefreshCw size={14} /> Refresh
            </button>
            <button className="btn" onClick={() => { setShowForm(true); setEditing(null); setForm({ name: '', description: '', type: 'cmd', content: '' }); setFormError(''); }}>
              <Plus size={14} /> New Script
            </button>
          </div>
        </div>

        {error && <p style={{ color: 'var(--danger)', marginBottom: '1rem' }}>{error}</p>}

        {showForm && (
          <div style={{ background: 'var(--bg-secondary)', borderRadius: '8px', padding: '1.5rem', marginBottom: '1.5rem', border: '1px solid var(--border)' }}>
            <h3 style={{ margin: '0 0 1rem 0' }}>{editing ? 'Edit Script' : 'New Script'}</h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
              <div style={{ display: 'flex', gap: '0.75rem' }}>
                <div style={{ flex: 1 }}>
                  <label style={{ display: 'block', marginBottom: '0.25rem', fontSize: '0.85rem', color: 'var(--text-secondary)' }}>Name</label>
                  <input type="text" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })}
                    style={{ width: '100%', padding: '0.5rem', background: 'var(--bg-primary)', border: '1px solid var(--border)', borderRadius: '4px', color: 'var(--text-primary)' }}
                    placeholder="e.g., Clear Temp Files" />
                </div>
                <div style={{ width: '120px' }}>
                  <label style={{ display: 'block', marginBottom: '0.25rem', fontSize: '0.85rem', color: 'var(--text-secondary)' }}>Type</label>
                  <select value={form.type} onChange={e => setForm({ ...form, type: e.target.value })}
                    style={{ width: '100%', padding: '0.5rem', background: 'var(--bg-primary)', border: '1px solid var(--border)', borderRadius: '4px', color: 'var(--text-primary)' }}>
                    <option value="cmd">cmd</option>
                    <option value="bat">bat</option>
                    <option value="ps1">ps1</option>
                  </select>
                </div>
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: '0.25rem', fontSize: '0.85rem', color: 'var(--text-secondary)' }}>Description</label>
                <input type="text" value={form.description} onChange={e => setForm({ ...form, description: e.target.value })}
                  style={{ width: '100%', padding: '0.5rem', background: 'var(--bg-primary)', border: '1px solid var(--border)', borderRadius: '4px', color: 'var(--text-primary)' }}
                  placeholder="Optional description" />
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: '0.25rem', fontSize: '0.85rem', color: 'var(--text-secondary)' }}>Content</label>
                <textarea value={form.content} onChange={e => setForm({ ...form, content: e.target.value })}
                  rows={form.type === 'cmd' ? 3 : 12}
                  style={{ width: '100%', padding: '0.5rem', background: 'var(--bg-primary)', border: '1px solid var(--border)', borderRadius: '4px', color: 'var(--text-primary)', fontFamily: 'monospace', resize: 'vertical', fontSize: '0.9rem', lineHeight: 1.5 }}
                  placeholder={form.type === 'cmd' ? 'e.g., echo hello' : form.type === 'bat' ? '@echo off\necho Hello from batch' : '# PowerShell script\nGet-Process | Sort-Object CPU -Descending | Select-Object -First 10'} />
              </div>
              {formError && <p style={{ color: 'var(--danger)', margin: 0, fontSize: '0.85rem' }}>{formError}</p>}
              <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.5rem' }}>
                <button className="btn" onClick={saveScript} disabled={saving}>
                  {saving ? 'Saving...' : (editing ? 'Update Script' : 'Create Script')}
                </button>
                <button className="btn btn-secondary" onClick={() => { setShowForm(false); setEditing(null); setFormError(''); }}>Cancel</button>
              </div>
            </div>
          </div>
        )}

        {loading ? <p style={{ color: 'var(--text-secondary)' }}>Loading...</p> : (
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ borderBottom: '1px solid var(--border)', textAlign: 'left' }}>
                <th style={{ padding: '0.5rem', color: 'var(--text-secondary)', fontWeight: 500 }}>Name</th>
                <th style={{ padding: '0.5rem', color: 'var(--text-secondary)', fontWeight: 500 }}>Type</th>
                <th style={{ padding: '0.5rem', color: 'var(--text-secondary)', fontWeight: 500 }}>Description</th>
                <th style={{ padding: '0.5rem', color: 'var(--text-secondary)', fontWeight: 500 }}>Last Updated</th>
                <th style={{ padding: '0.5rem', color: 'var(--text-secondary)', fontWeight: 500 }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {scripts.length === 0 && (
                <tr><td colSpan={5} style={{ padding: '2rem', textAlign: 'center', color: 'var(--text-secondary)' }}>No scripts yet. Click "New Script" to create one.</td></tr>
              )}
              {scripts.map((s: any) => (
                <tr key={s.id} style={{ borderBottom: '1px solid var(--glass-border)' }}>
                  <td style={{ padding: '0.75rem 0.5rem', fontWeight: 500 }}>{s.name}</td>
                  <td style={{ padding: '0.75rem 0.5rem' }}>
                    <span style={{ background: typeBadgeColor[s.type] || 'var(--bg-elevated)', color: '#fff', padding: '2px 8px', borderRadius: '4px', fontSize: '0.75rem', fontWeight: 600 }}>
                      {s.type}
                    </span>
                  </td>
                  <td style={{ padding: '0.75rem 0.5rem', color: 'var(--text-secondary)', fontSize: '0.85rem' }}>{s.description || '\u2014'}</td>
                  <td style={{ padding: '0.75rem 0.5rem', color: 'var(--text-secondary)', fontSize: '0.85rem' }}>{new Date(s.updatedAt).toLocaleString()}</td>
                  <td style={{ padding: '0.75rem 0.5rem' }}>
                    <div style={{ display: 'flex', gap: '0.25rem' }}>
                      <button className="btn btn-secondary" style={{ padding: '4px 8px', fontSize: '0.75rem' }}
                        onClick={() => { setEditing(s); setForm({ name: s.name, description: s.description, type: s.type, content: s.content }); setShowForm(true); setFormError(''); }}>
                        <Edit3 size={12} /> Edit
                      </button>
                      <button className="btn btn-secondary" style={{ padding: '4px 8px', fontSize: '0.75rem', color: 'var(--danger)' }}
                        onClick={() => deleteScript(s.id)}>
                        <Trash2 size={12} /> Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
